import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';

/**
 * Weidner override of the Aster VirtualTourEmbed. The parent ships a native
 * <select> filled with primary + hard-coded white — the option list is
 * unstyleable OS chrome and the trigger doesn't share this theme's typewriter
 * uppercase / sharp-corner language. This copy keeps the same embed and
 * collection binding, and replaces the selector with a custom listbox that
 * matches gallery tabs, header dropdowns, and HeroSolidBG CTAs.
 */

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

function tourLabel(tour: VirtualTour, index: number): string {
  return tour.virtual_tour_title || `Tour ${index + 1}`;
}

function TourPicker({
  tours,
  selectedIndex,
  onSelect,
}: {
  tours: VirtualTour[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(selectedIndex);
  const highlightRef = useRef(selectedIndex);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const moveHighlight = useCallback((next: number) => {
    highlightRef.current = next;
    setHighlight(next);
  }, []);

  useEffect(() => {
    if (!open) return;
    moveHighlight(selectedIndex);

    function handlePointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const current = highlightRef.current;
        moveHighlight((current + delta + tours.length) % tours.length);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        moveHighlight(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        moveHighlight(tours.length - 1);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(highlightRef.current);
        close();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, selectedIndex, tours.length, onSelect, close, moveHighlight]);

  useEffect(() => {
    if (!open) return;
    const option = listRef.current?.querySelector<HTMLElement>(
      `[data-tour-index="${highlight}"]`,
    );
    option?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const activeLabel = tourLabel(tours[selectedIndex] || tours[0], selectedIndex);

  return (
    <div className="vt-tour-picker relative" ref={rootRef}>
        <button
          type="button"
          className={cn('vt-tour-picker__trigger', open && 'is-open')}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Select a tour"
          onClick={() => setOpen((current) => !current)}
        >
          <span className="vt-tour-picker__value">{activeLabel}</span>
          <svg
            aria-hidden="true"
            className={cn(
              'vt-tour-picker__chevron',
              open && 'vt-tour-picker__chevron--open',
            )}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="square"
            strokeLinejoin="miter"
          >
            <path d="M5 8l5 5 5-5" />
          </svg>
        </button>

        <ul
          ref={listRef}
          role="listbox"
          aria-label="Select a tour"
          className={cn(
            'vt-tour-picker__menu',
            open
              ? 'opacity-100 visible translate-y-0'
              : 'opacity-0 invisible pointer-events-none -translate-y-1',
          )}
        >
          {tours.map((tour, i) => {
            const selected = i === selectedIndex;
            const highlighted = i === highlight;
            return (
              <li key={`${tour.virtual_tour_url || 'tour'}-${i}`} role="presentation">
                <button
                  type="button"
                  role="option"
                  data-tour-index={i}
                  aria-selected={selected}
                  className={cn(
                    'vt-tour-picker__option',
                    selected && 'is-selected',
                    highlighted && 'is-highlighted',
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => {
                    onSelect(i);
                    close();
                  }}
                >
                  <span className="vt-tour-picker__option-label">
                    {tourLabel(tour, i)}
                  </span>
                  {selected && (
                    <svg
                      aria-hidden="true"
                      className="vt-tour-picker__check"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="square"
                    >
                      <path d="M3 8.5l3.5 3.5L13 4.5" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
    </div>
  );
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const safeIndex = validTours.length
    ? Math.min(selectedIndex, validTours.length - 1)
    : 0;
  const activeTour = validTours[safeIndex];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-background py-8 md:py-12">
      <EditableCollectionBinding
        bindingPath="virtualtourembed.tours"
        label="Virtual Tours"
      >
        {hasMultiple && (
          <div className="mx-auto mb-6 flex max-w-6xl justify-end px-4 sm:px-6 lg:px-8">
            <TourPicker
              tours={validTours}
              selectedIndex={safeIndex}
              onSelect={setSelectedIndex}
            />
          </div>
        )}

        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {activeTour?.virtual_tour_url ? (
            <div
              className="relative w-full overflow-hidden border border-border"
              style={{ paddingBottom: '56.25%', borderRadius: 2 }}
            >
              <iframe
                key={activeTour.virtual_tour_url}
                src={activeTour.virtual_tour_url}
                className="absolute inset-0 h-full w-full border-0"
                allowFullScreen
                title={activeTour.virtual_tour_title || 'Virtual Tour'}
              />
            </div>
          ) : (
            <div
              className="flex aspect-video w-full items-center justify-center bg-muted"
              style={{ borderRadius: 2 }}
            >
              <p className="font-body text-muted-foreground">
                No virtual tours are available for this property.
              </p>
            </div>
          )}
        </div>
      </EditableCollectionBinding>

      <style>{`
        .vt-tour-picker {
          width: 100%;
          max-width: 22rem;
        }

        .vt-tour-picker__trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: 100%;
          padding: 0.85rem 1.15rem;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .vt-tour-picker__trigger:hover,
        .vt-tour-picker__trigger.is-open {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 85%, #000);
        }
        .vt-tour-picker__trigger:focus-visible {
          outline: 2px solid var(--vibe-color-secondary);
          outline-offset: 3px;
        }

        .vt-tour-picker__value {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .vt-tour-picker__chevron {
          width: 1rem;
          height: 1rem;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }
        .vt-tour-picker__chevron--open {
          transform: rotate(180deg);
        }

        .vt-tour-picker__menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          z-index: 30;
          margin: 0;
          padding: 0.25rem 0;
          list-style: none;
          background: var(--vibe-color-background);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          box-shadow: var(--vibe-shadow-lg);
          max-height: 16rem;
          overflow-y: auto;
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
        }

        .vt-tour-picker__option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          width: 100%;
          padding: 0.75rem 1.15rem;
          background: transparent;
          border: 0;
          border-left: 3px solid transparent;
          color: var(--vibe-color-text);
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .vt-tour-picker__option.is-highlighted:not(.is-selected) {
          background: var(--vibe-color-surface);
          border-left-color: var(--vibe-color-primary);
        }
        .vt-tour-picker__option.is-selected {
          background: var(--vibe-color-secondary);
          border-left-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .vt-tour-picker__option.is-selected.is-highlighted {
          background: color-mix(in srgb, var(--vibe-color-secondary) 88%, #000);
        }

        .vt-tour-picker__option-label {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .vt-tour-picker__check {
          width: 0.85rem;
          height: 0.85rem;
          flex-shrink: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default VirtualTourEmbed;
